import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Clock, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSearchHistory } from '@/lib/hooks/use-search-history';

const HOT_STOCKS = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA'];

export default function HomePage() {
  const { t } = useTranslation();
  const [ticker, setTicker] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const navigate = useNavigate();
  const { history, removeFromHistory, clearHistory } = useSearchHistory();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredHistory = ticker.trim()
    ? history.filter(
        (item) =>
          item.ticker.includes(ticker.toUpperCase()) ||
          (item.name && item.name.toLowerCase().includes(ticker.toLowerCase()))
      )
    : history;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedIndex >= 0 && selectedIndex < filteredHistory.length) {
      handleSelectHistory(filteredHistory[selectedIndex].ticker);
      return;
    }
    if (ticker.trim()) {
      setShowHistory(false);
      navigate(`/stock/${ticker.toUpperCase()}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showHistory || filteredHistory.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < filteredHistory.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Escape') {
      setShowHistory(false);
      setSelectedIndex(-1);
    }
  };

  const handleSelectHistory = (selectedTicker: string) => {
    setTicker(selectedTicker);
    setShowHistory(false);
    setSelectedIndex(-1);
    navigate(`/stock/${selectedTicker}`);
  };

  const handleRemoveHistory = (e: React.MouseEvent, tickerToRemove: string) => {
    e.stopPropagation();
    removeFromHistory(tickerToRemove);
  };

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowHistory(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-32">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-slate-900 mb-4">
            {t('index.title')}
          </h1>
          <p className="text-xl text-slate-600 mb-12">
            {t('index.description')}
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-md mx-auto">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder={t('index.searchPlaceholder')}
                  value={ticker}
                  onChange={(e) => {
                    setTicker(e.target.value.toUpperCase());
                    setShowHistory(true);
                    setSelectedIndex(-1);
                  }}
                  onKeyDown={handleKeyDown}
                  onFocus={() => history.length > 0 && setShowHistory(true)}
                  className="pl-10 h-12 text-lg"
                />

                {/* History Dropdown */}
                {showHistory && filteredHistory.length > 0 && (
                  <div
                    ref={dropdownRef}
                    className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 z-50 overflow-hidden"
                  >
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {t('index.recentSearches', '最近搜索')}
                      </span>
                      {!ticker && (
                        <button
                          type="button"
                          onClick={clearHistory}
                          className="text-xs text-slate-400 hover:text-slate-600"
                        >
                          {t('index.clearAll', '清除全部')}
                        </button>
                      )}
                    </div>
                    <ul className="max-h-64 overflow-y-auto text-left">
                      {filteredHistory.map((item, index) => (
                        <li
                          key={item.ticker}
                          onClick={() => handleSelectHistory(item.ticker)}
                          onMouseEnter={() => setSelectedIndex(index)}
                          className={`flex items-center justify-between px-3 py-2 cursor-pointer group ${
                            index === selectedIndex ? 'bg-slate-100' : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{item.ticker}</span>
                            <span className="text-sm text-slate-500">{item.name}</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => handleRemoveHistory(e, item.ticker)}
                            className="p-1 rounded hover:bg-slate-200 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3 text-slate-400" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <Button type="submit" size="lg" className="h-12 px-8">
                {t('index.analyzeButton')}
              </Button>
            </div>
          </form>

          {/* Hot Stocks */}
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <span className="text-sm text-slate-500 py-1">{t('index.hotStocks', 'Hot Stocks')}:</span>
            {HOT_STOCKS.map((stock) => (
              <button
                key={stock}
                onClick={() => navigate(`/stock/${stock}`)}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
              >
                {stock}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
